'use client';

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '../Button/Button';
import type { ButtonProps } from '../Button/Button';
import styles from './ExportDropdown.module.css';

export interface ExportDropdownProps {
	onExportExcel: () => void;
	onExportPdf: () => void;
	className?: string;
	buttonClassName?: string;
	variant?: ButtonProps['variant'];
	label?: string;
}

export const ExportDropdown = ({
	onExportExcel,
	onExportPdf,
	className,
	buttonClassName,
	variant = 'secondary',
	label = 'Exportar',
}: ExportDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) return;
		const handleClickOutside = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const handleExcel = () => {
		setIsOpen(false);
		onExportExcel();
	};

	const handlePdf = () => {
		setIsOpen(false);
		onExportPdf();
	};

	return (
		<div className={`${styles.wrapper} ${className ?? ''}`} ref={wrapperRef}>
			<Button
				variant={variant}
				className={`${styles.button} ${buttonClassName ?? ''}`}
				onClick={() => setIsOpen((prev) => !prev)}
			>
				{label}
				<ChevronDown
					className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
				/>
			</Button>
			{isOpen && (
				<div className={styles.dropdown}>
					<button type="button" className={styles.option} onClick={handleExcel}>
						<FileSpreadsheet className={styles.icon} />
						Excel (.xlsx)
					</button>
					<button type="button" className={styles.option} onClick={handlePdf}>
						<FileText className={styles.icon} />
						PDF (.pdf)
					</button>
				</div>
			)}
		</div>
	);
};
